/**
 * Client-side pricing helper
 *
 * Fetches packages and add-ons from the server's canonical source
 * (server/config/pricing.js via GET /api/pricing/all) and caches them in
 * memory for the lifetime of the page.
 *
 * Usage:
 *   import { getPricing, getDefaultPackages, getDefaultAddOns } from './utils/pricing';
 *   const { packages, addOns } = await getPricing();
 *
 * If the network request fails, returns hardcoded fallback data so the
 * booking flow continues working offline / with API outages.
 */

let cachedPricing = null;
let inflightRequest = null;

/** The shape every caller gets, from a /api/pricing/all `data` payload. */
function normalizePricing(data) {
  return { packages: data.packages, addOns: data.addOns, sqftSurcharge: data.sqftSurcharge || null, landAcreage: data.landAcreage || null, mediaExpiryDays: Number(data.mediaExpiryDays) > 0 ? Number(data.mediaExpiryDays) : 45, mediaExtension: data.mediaExtension || null, travel: data.travel || null };
}

/**
 * The server renderer (api/render.js) prints the live pricing it used into
 * #urp-pricing (2026-10-17), so the page starts with the same prices the raw
 * HTML shows: no "Loading prices", no layout jump, no second request.
 */
function readBootstrapPricing() {
  try {
    const el = typeof document !== 'undefined' && document.getElementById('urp-pricing');
    const data = el ? JSON.parse(el.textContent || 'null') : null;
    return data && Array.isArray(data.packages) && data.packages.length && Array.isArray(data.addOns) ? normalizePricing(data) : null;
  } catch (e) {
    return null;
  }
}
cachedPricing = readBootstrapPricing();
// Bootstrapped prices are for the first paint; the first getPricing() still
// fetches once, so a page served from the edge cache can't show a stale price.
let bootstrapped = !!cachedPricing;

/** Live pricing if it is already known (bootstrapped or fetched), else null. */
export function getCachedPricing() {
  return cachedPricing;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Hardcoded fallback — used ONLY when the API is unreachable. Keep in sync
 * with server/config/pricing.js. The server is the source of truth; this
 * fallback exists so the UI stays usable during outages.
 */
const FALLBACK_PACKAGES = [
  {
    _id: 'basic', name: 'Essential Package', price: 150, category: 'home',
    // inclusions mirror server/config/pricing.js (includedAddOns and the {qty:…}
    // numbers are derived from them below).
    inclusions: [],
    features: [
      'Up to 25 HDR photos',
      'Interior & exterior coverage',
      'MLS-ready formatting',
      'Next-business-day delivery',
      'Online gallery with downloads',
      '*Up to 2,500 sq ft — +$50 & +10 photos per additional 1,000 sq ft'
    ],
    popular: false, active: true,
    description: 'Great for standard listings'
  },
  {
    _id: 'standard', name: 'Professional Package', price: 275, category: 'home',
    // inclusions mirror server/config/pricing.js (includedAddOns and the {qty:…}
    // numbers are derived from them below).
    inclusions: [{ kind: 'drone', qty: 5 }, { kind: 'twilight', qty: 1 }],
    features: [
      'Up to 40 HDR photos',
      'Interior & exterior coverage',
      '{qty:drone} aerial drone photos',
      '{qty:twilight} virtual twilight conversion',
      '24-hour delivery',
      'Online gallery with downloads',
      '*Up to 2,500 sq ft — +$50 & +10 photos per additional 1,000 sq ft'
    ],
    popular: true, active: true,
    description: 'Most popular — covers all the bases'
  },
  {
    _id: 'premium', name: 'Premium Package', price: 450, category: 'home',
    // inclusions mirror server/config/pricing.js (includedAddOns and the {qty:…}
    // numbers are derived from them below).
    inclusions: [{ kind: 'drone', qty: 12 }, { kind: 'twilight', qty: 3 }, { kind: 'staging', qty: 1 }, { id: 'video-30-agent' }, { id: 'floor-plan-2d' }],
    features: [
      'Up to 45 HDR photos',
      'Interior & exterior coverage',
      '{qty:drone} aerial drone photos',
      '{qty:twilight} virtual twilight conversions',
      '30-second Social Reel',
      'Virtual staging ({qty:staging} photo)',
      '2D floor plan with measurements',
      'Custom property listing page',
      'Online gallery with downloads'
    ],
    popular: false, active: true,
    description: 'Stand out with full media coverage'
  },
  {
    _id: 'luxury', name: 'Luxury Package', price: 795, category: 'home',
    // inclusions mirror server/config/pricing.js (includedAddOns and the {qty:…}
    // numbers are derived from them below).
    inclusions: [{ kind: 'drone', qty: 20 }, { kind: 'twilight', qty: 5 }, { kind: 'staging', qty: 3 }, { id: 'matterport-under-3000' }, { id: 'video-full' }, { id: 'floor-plan-2d' }],
    features: [
      'Up to 50 HDR photos',
      'Interior & exterior coverage',
      'Up to {qty:drone} aerial drone photos',
      '{qty:twilight} virtual twilight conversions',
      'Matterport 3D tour',
      'Cinematic video (up to 2 minutes)',
      'Virtual staging ({qty:staging} photos)',
      '2D floor plan with measurements',
      'Dedicated editor',
      'Custom property listing page',
      'Online gallery with downloads'
    ],
    popular: false, active: true,
    description: 'The ultimate marketing package'
  },
  // Land (2026-10-11, per-acre since 2026-10-12) — mirrors server/config/pricing.js.
  // Shown instead of the home packages when "Land or lot" is chosen.
  {
    _id: 'land-lot', name: 'Land Package', price: 195, category: 'land', pricedPerAcre: true,
    inclusions: [],
    features: [
      'Aerial photos covering the whole parcel — at least 10, plus 5 more for each additional acre',
      'Boundary lines drawn on 3 aerials',
      '10 ground photos — frontage, access, terrain and views',
      '24-hour delivery',
      'Online gallery with downloads',
      '*$195 covers up to 1 acre; each additional acre (or part of one) adds $100. Boundary lines are drawn from county parcel maps and are approximate.'
    ],
    popular: false, active: true,
    description: 'Vacant lots and acreage — priced by size'
  }
];

const FALLBACK_ADDONS = [
  { id: 'extra-photos-10', name: 'Extra 10 Photos', price: 30, description: 'Additional HDR photos beyond package', icon: '📷', land: true },
  { id: 'virtual-twilight-1', name: 'Virtual Twilight (1 photo)', price: 25, description: 'Day-to-dusk conversion — no second visit needed', icon: '🌅', land: true },
  { id: 'virtual-twilight-3', name: 'Virtual Twilight (3 photos)', price: 65, description: 'Three day-to-dusk conversions', icon: '🌅', land: true },
  { id: 'drone-5', name: 'Drone Photos (5 shots)', price: 95, description: 'Aerial stills of property & surroundings', icon: '🚁', land: true },
  { id: 'drone-12', name: 'Drone Photos (12 shots)', price: 150, description: 'Extended aerial coverage with multiple angles', icon: '🚁', land: true },
  // Mirrors server/config/pricing.js. 'video-30' (30-sec Drone) and 'video-60'
  // (60-sec Trailer) retired 2026-07-31; the server keeps them resolvable for
  // historical bookings via RETIRED_ADD_ONS. This is the offline fallback only
  // — the live menu comes from GET /api/pricing/all.
  { id: 'video-30-agent', name: 'Video — 30-sec Social Reel', price: 145, description: 'Vertical, music-driven walk-through reel — made for Instagram & TikTok', icon: '🎬', land: true },
  { id: 'video-agent-hosted', name: 'Video — 60-sec Agent On Camera', price: 395, description: 'You on camera introducing the home, mic\'d, with B-roll cutaways — 60 seconds', icon: '🎥' },
  { id: 'video-full', name: 'Video — Cinematic Tour (up to 2 min)', price: 345, description: 'Complete interior and exterior cinematic walkthrough', icon: '🎬', land: true },
  { id: 'virtual-staging-1', name: 'Virtual Staging (per photo)', price: 45, description: 'Digitally furnish a vacant room with realistic decor', icon: '🛋️' },
  { id: 'matterport-under-3000', name: 'Matterport 3D Tour (up to 3,000 sq ft)', price: 199, description: 'Interactive 3D walkthrough with dollhouse view', icon: '🏠' },
  { id: 'matterport-over-3000', name: 'Matterport 3D Tour (over 3,000 sq ft)', price: 300, description: 'Interactive 3D walkthrough for larger properties', icon: '🏠' },
  { id: 'floor-plan-2d', name: '2D Floor Plan', price: 95, description: 'Professional 2D floor plan with measurements', icon: '📐' }
];

/**
 * Fetch packages and add-ons from the server. Result is cached in module memory
 * so subsequent calls are instant. Concurrent callers share the in-flight request.
 */
export async function getPricing() {
  if (cachedPricing && !bootstrapped) return cachedPricing;
  if (inflightRequest) return inflightRequest;

  inflightRequest = (async () => {
    try {
      const res = await fetch(`${API_URL}/pricing/all`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const data = json?.data;
      if (!data || !Array.isArray(data.packages) || !Array.isArray(data.addOns)) {
        throw new Error('Malformed pricing response');
      }
      cachedPricing = normalizePricing(data);
      bootstrapped = false;
      return cachedPricing;
    } catch (err) {
      console.warn('Pricing fetch failed, using fallback:', err.message);
      // Keep the server-printed live prices over the bundled ones.
      if (!bootstrapped) cachedPricing = { packages: FALLBACK_PACKAGES, addOns: FALLBACK_ADDONS };
      bootstrapped = false;
      return cachedPricing;
    } finally {
      inflightRequest = null;
    }
  })();

  return inflightRequest;
}

/**
 * Synchronous helper for components that need add-ons before the async fetch
 * resolves. Returns cached add-ons if available, otherwise the fallback.
 */
export function getDefaultAddOns() {
  return cachedPricing?.addOns || FALLBACK_ADDONS;
}

/**
 * Synchronous helper for components that need packages immediately.
 */
export function getDefaultPackages() {
  return cachedPricing?.packages || FALLBACK_PACKAGES;
}

/**
 * Land (2026-10-11, per-acre since 2026-10-12). Packages carry `category`
 * ('home' | 'land'); add-ons that make sense on a land shoot carry
 * `land: true`. MUST mirror LAND_ACREAGE and the calc in server/config/pricing.js
 * — the server recomputes authoritatively on submit.
 */
export const LAND_ACREAGE = { includedAcres: 1, pricePerAcre: 100, aerialPhotosIncluded: 10, aerialPhotosPerAcre: 5 };
const activeLandConfig = () => (cachedPricing && cachedPricing.landAcreage) ? cachedPricing.landAcreage : LAND_ACREAGE;
export const isLandPackage = (pkg) => !!pkg && pkg.category === 'land';
export const packagesFor = (packages, land) => (packages || []).filter(p => p && (p.category === 'land') === !!land);
export const addOnsFor = (addOns, land) => (addOns || []).filter(a => a && (!land || a.land === true));
/** Parse an acres field ("0.46", ".5", "2") — null when blank or not a size. */
export const parseAcres = (v) => {
  const n = parseFloat(String(v == null ? '' : v).replace(/[,\s]/g, ''));
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : null;
};
/** Whole acres charged above the included acre ("or part of one" rounds up). */
export const landExtraAcres = (acres, config) => {
  const cfg = config || activeLandConfig();
  const a = parseAcres(acres);
  if (a === null || a <= cfg.includedAcres) return 0;
  return Math.ceil(Math.round((a - cfg.includedAcres) * 100) / 100);
};
export const calculateAcreageSurcharge = (pkg, acres, config) => {
  const cfg = config || activeLandConfig();
  return pkg && pkg.pricedPerAcre ? landExtraAcres(acres, cfg) * cfg.pricePerAcre : 0;
};
export const landAerialPhotoMinimum = (acres, config) => {
  const cfg = config || activeLandConfig();
  return cfg.aerialPhotosIncluded + landExtraAcres(acres, cfg) * cfg.aerialPhotosPerAcre;
};
export const getLandAcreageConfig = () => activeLandConfig();

/**
 * Pre-warm the cache. Call once on app load (e.g. in App.js useEffect).
 */
export function preloadPricing() {
  return getPricing();
}

/**
 * Square-footage surcharge — MUST mirror server/config/pricing.js.
 * Essential/Professional cover up to 2,500 sq ft; each additional 1,000 sq ft
 * (or part) above that adds $50 and 10 photos. Pack Q: the live rate/threshold/
 * packages are admin-configurable and arrive via GET /pricing/all, cached here.
 * The calc functions read the cached admin config when present, else these
 * defaults; the server recomputes authoritatively on submit.
 */
// Package inclusions (2026-10-17) — mirror of server/config/pricing.js.
// includedAddOns is derived, never typed; the blogagent/prerender suite checks
// server and client agree.
export const ADD_ON_UNITS = {
  'drone-5': { kind: 'drone', qty: 5 },
  'drone-12': { kind: 'drone', qty: 12 },
  'virtual-twilight-1': { kind: 'twilight', qty: 1 },
  'virtual-twilight-3': { kind: 'twilight', qty: 3 },
  'virtual-staging-1': { kind: 'staging', qty: 1 }
};

export function deriveIncludedAddOns(inclusions) {
  const ids = [];
  for (const inc of (Array.isArray(inclusions) ? inclusions : [])) {
    if (inc.id) ids.push(inc.id);
    else for (const [id, u] of Object.entries(ADD_ON_UNITS)) if (u.kind === inc.kind && u.qty <= inc.qty) ids.push(id);
  }
  return [...new Set(ids)];
}

/** "{qty:drone} aerial drone photos" → "20 aerial drone photos" (mirror of the server). */
export function fillInclusionQty(text, inclusions) {
  return String(text).replace(/\{qty:([a-z]+)\}/g, (m, kind) => {
    const inc = (inclusions || []).find(i => i.kind === kind);
    return inc ? String(inc.qty) : m;
  });
}
for (const pkg of FALLBACK_PACKAGES) {
  pkg.includedAddOns = deriveIncludedAddOns(pkg.inclusions);
  pkg.features = pkg.features.map(f => fillInclusionQty(f, pkg.inclusions));
}

/** One-off add-ons a package includes (never charged again with it). */
export function includedOnceIds(pkg) {
  return (pkg && Array.isArray(pkg.inclusions) ? pkg.inclusions : []).filter(i => i.id).map(i => i.id);
}

/** Units of a counted add-on's kind the package includes (0 when none). */
export function includedUnitsFor(pkg, addOnId) {
  const u = ADD_ON_UNITS[addOnId];
  if (!u || !pkg || !Array.isArray(pkg.inclusions)) return 0;
  const inc = pkg.inclusions.find(i => i.kind === u.kind);
  return inc ? inc.qty : 0;
}

export const SQFT_SURCHARGE = {
  threshold: 2500,
  incrementSize: 1000,
  incrementPrice: 50,
  photosPerIncrement: 10,
  appliesTo: ['basic', 'essential', 'standard', 'professional']
};

function activeSurchargeConfig() {
  return (cachedPricing && cachedPricing.sqftSurcharge) ? cachedPricing.sqftSurcharge : SQFT_SURCHARGE;
}

function sqftBands(packageId, squareFootage, config) {
  const cfg = config || activeSurchargeConfig();
  if (!packageId) return 0;
  const key = String(packageId).toLowerCase();
  if (!cfg.appliesTo || !cfg.appliesTo.includes(key)) return 0;
  const sqft = parseInt(squareFootage, 10);
  if (isNaN(sqft) || sqft <= cfg.threshold) return 0;
  return Math.ceil((sqft - cfg.threshold) / cfg.incrementSize);
}

export function calculateSqftSurcharge(packageId, squareFootage, config) {
  const cfg = config || activeSurchargeConfig();
  return sqftBands(packageId, squareFootage, cfg) * cfg.incrementPrice;
}

export function calculateSqftBonusPhotos(packageId, squareFootage, config) {
  const cfg = config || activeSurchargeConfig();
  return sqftBands(packageId, squareFootage, cfg) * (cfg.photosPerIncrement || 0);
}

/**
 * Days delivered media stays online. Served alongside pricing so the expiry
 * disclosure in the booking flow renders the live Settings value rather than a
 * literal that silently goes stale the first time the setting changes.
 */
export function getMediaExpiryDays() {
  return (cachedPricing && cachedPricing.mediaExpiryDays) ? cachedPricing.mediaExpiryDays : 45;
}
